import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  Radio,
  Divider,
  Button,
  Chip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useQuestion } from "../../context/QuestionsContext";
import { useNavigate } from "react-router-dom";
import ROUTES_NAME from "../../constants/RoutesName";
import Loader from "../../components/Loader";
import {
  getSelectedSubjects,
  setSelectedSubjects,
} from "../../services/service";
import SubmissionPopupReLogin from "../../components/popups/SubmissionPopupReLogin";
import SubmissionPopup from "../../components/popups/SubmissionPopup";
import { useQuestionSection } from "../../hooks/default/useQuestionSection";


function SubjectSelection() {
  const { question, fetchQuestions, loading } = useQuestion();
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [submittedSubject, setSubmittedSubject] = useState(null);
  const [totalSelectedSubject, setTotalSelectedSubject] = useState(0);
  const [isSelectLoading, setIsSelectLoading] = useState(false);
  const navigate = useNavigate();

  const {
    showSubmissionPopup,
    showSubmissionPopupReLogin,
    countdownByHead,
    stats,
  } = useQuestionSection({});

  const subjects = useMemo(() => {
    return question?.subjects?.map((subject) => {
      let totalQuestions = 0;

      const sections = subject.sections.map((section) => {
        totalQuestions += section.questions.length;

        return {
          section_id: section.id,
          section_name: section.section_name,
          question_count: section.questions.length,
        };
      });

      return {
        id: subject.id,
        name: subject.subject_name,
        questions: totalQuestions,
        sections,
      };
    });
  }, [question]);

  const handleSelect = (id) => {
    setSelectedSubject(id);
    const sectionId = subjects.filter(
      (subject) => Number(subject.id) === Number(id),
    )[0]?.sections[0].section_id;
    setSelectedSection(sectionId);
  };

  const handleAttempt = async () => {
    const examId = sessionStorage.getItem("examId");
    setIsSelectLoading(true);
    try {
      const res = await setSelectedSubjects(examId, selectedSubject);
      if (res.success) {
        sessionStorage.setItem("selectedSubject", selectedSubject);
        sessionStorage.setItem("selectedSection", selectedSection);
        sessionStorage.setItem("examStarted", true);
        sessionStorage.removeItem("lastAttempted");
        if (totalSelectedSubject + 1 === subjects.length) {
          sessionStorage.setItem("lastSubject", true);
        }

        navigate(ROUTES_NAME.TEST);
        setIsSelectLoading(false);
      }
    } catch (error) {
      console.log(error);
      setIsSelectLoading(false);
    }
  };

  const fetchSubmittedSubject = async () => {
    try {
      const res = await getSelectedSubjects();
      const selected = res.submit_subjects.filter(
        (subject) => subject.status === "selected",
      );
      const submitted = res.submit_subjects.filter(
        (subject) => subject.status === "submitted",
        (subject) => subject.status === "submitted",
      );

      setSubmittedSubject(submitted);
      setTotalSelectedSubject(selected.length + submitted.length);
      if (selected.length + submitted.length === subjects.length) {
        sessionStorage.setItem("lastSubject", true);
      }

      if (selected.length > 0) {
        sessionStorage.setItem("selectedSubject", selected[0].subject_id.id);
        const sectionId = subjects.filter(
          (subject) => Number(subject.id) === Number(selected[0].subject_id.id),
        )[0]?.sections[0].section_id;
        sessionStorage.setItem("selectedSection", sectionId);
        sessionStorage.setItem("examStarted", true);
        navigate(ROUTES_NAME.TEST);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchQuestions(sessionStorage.getItem("examId"));
    fetchSubmittedSubject();
  }, []);

  return (
    <Box sx={{ background: "white", height: "100vh", width: "100vw" }}>
      {loading || isSelectLoading ? (
        <Loader />
      ) : (
        <Box sx={{ p: 4, maxWidth: 1100, margin: "auto" }}>
          {/* Attempted Subjects */}
          <Card sx={{ p: 3, mb: 3 }}>
            <Box display="flex" justifyContent="space-between">
              <Typography fontWeight="600">Your Attempted Subjects</Typography>

              <Chip
                label={`${submittedSubject?.length || 0} attempted`}
                color="success"
                size="small"
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            {submittedSubject?.map((subject) => (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  border: "1px solid #e0e0e0",
                  p: 1.5,
                  borderRadius: 2,
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <CheckCircleIcon color="success" />
                  <Typography>{subject.subject_id.subject_name}</Typography>
                </Box>

                <Chip label="Completed" size="small" color="success" />
              </Box>
            ))}
          </Card>

          {/* Subject Table */}
          <Card sx={{ p: 3 }}>
            <Grid
              container
              sx={{
                fontWeight: 600,
                color: "#6b7280",
                pb: 2,
              }}
            >
              <Grid item xs={5}>
                <Typography>SUBJECT NAME</Typography>
              </Grid>
            </Grid>

            <Divider />

            {subjects?.map((subject) => {
              const isSubmitted = submittedSubject?.find(
                (item) => item.subject_id.id === subject.id,
              );

              if (isSubmitted) return null; // do not render if present

              return (
                <Box key={subject.id}>
                  <Grid container alignItems="center" sx={{ py: 2 }}>
                    <Grid item xs={5} display="flex" alignItems="center">
                      <Radio
                        checked={selectedSubject === subject.id}
                        onChange={() => handleSelect(subject.id)}
                      />

                      <Typography sx={{ textTransform: "uppercase" }}>
                        {subject.name}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Divider />
                </Box>
              );
            })}
          </Card>

          {/* Button */}
          <Box sx={{ mt: 3, float: "right" }}>
            <Button
              variant="contained"
              disabled={!selectedSubject}
              onClick={handleAttempt}
            >
              Select Subject
            </Button>
          </Box>
        </Box>
      )}
      <SubmissionPopupReLogin
        open={showSubmissionPopupReLogin}
        countdown={countdownByHead}
      />

      <SubmissionPopup
        open={showSubmissionPopup}
        countdown={countdownByHead}
        stats={stats}
      />
    </Box>
  );
}

export default SubjectSelection;
